import { DEMO_PLANS } from 'pages/Operations/Plans/plansDemoData'
import { IOpsDepartment } from 'types/opsDepartments.interface'
import {
  IOpsTodayCardTrend,
  IOpsTodayInboxItem,
  IOpsTodayQuestionCard,
} from 'types/opsToday.interface'
import { PLAN_STATUS_TO_CORE, taskCoreState } from '../Shared/opsControl'
import { OPS_AVAIL_HISTORY, OPS_PACE_HISTORY } from './todayEnviron'
import {
  OPS_ALERTS,
  OPS_LIVE_TASKS,
  OPS_LOCATION_STATUS,
  OPS_MACHINES,
  OPS_MACHINES_DOWN,
  OPS_PARTS_RISK,
  OPS_RISKS,
  OPS_SECURITY_POSTS,
} from '../Shared/opsControlMocks'

const count = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`

const buildTrend = (values: number[], label: string): IOpsTodayCardTrend => {
  const avg = values.reduce((sum, v) => sum + v, 0) / values.length
  return {
    chips: values.map((v, i) => `${v >= (i === 0 ? avg : values[i - 1]) ? '▲' : '▼'}${v}`),
    note: `${label} · avg ${Math.round(avg)}%`,
  }
}

const paceTrend = () => buildTrend(OPS_PACE_HISTORY, '5-shift history')

const availTrend = () => buildTrend(OPS_AVAIL_HISTORY, '5-shift availability')

const deptTasks = (key: string) => OPS_LIVE_TASKS.filter((t) => t.department === key)

const buildPaceCard = (): IOpsTodayQuestionCard => {
  const activePlans = DEMO_PLANS.filter((p) => PLAN_STATUS_TO_CORE[p.status] === 'ACTIVE')
  const behind = activePlans.find((p) => p.queueHealth === 2)
  return {
    id: 'pace',
    question: 'Are we on pace?',
    sentence: behind ? `Falling behind on ${behind.name}` : 'Yes — on track for 18:00',
    caption: behind
      ? '1,840 of 2,100 t · at this rate ~1,950 t by 18:00 — 150 t short'
      : '1,840 of 2,100 t · on pace to finish ~17:20',
    tone: behind ? 'warning' : 'success',
    linkPath: '/operations/plans',
    trend: paceTrend(),
  }
}

const buildBrokenCard = (): IOpsTodayQuestionCard => {
  const blocked = OPS_LIVE_TASKS.filter((t) => taskCoreState(t.taskStatus) === 'BLOCKED')
  const down = OPS_MACHINES_DOWN
  const broken = down.length > 0 || blocked.length > 0
  const available = OPS_MACHINES.filter((m) => m.state === 'active').length
  const availability = `${available} of ${OPS_MACHINES.length} machines available (${Math.round(
    (available / OPS_MACHINES.length) * 100,
  )}%)`
  return {
    id: 'broken',
    question: 'Anything broken?',
    sentence: broken
      ? `${count(down.length, 'machine')} down, ${count(blocked.length, 'task')} blocked`
      : 'No — everything is running.',
    caption: broken
      ? [availability, ...down.map((m) => `${m.name} back ~${m.estimatedReturn}`)].join(' · ')
      : `${availability} · every machine is on the job.`,
    tone: broken ? 'danger' : 'success',
    linkPath: '/operations/timeline',
    trend: availTrend(),
  }
}

const buildMissingCard = (): IOpsTodayQuestionCard => {
  const muster = '18 of 20 checked in on site'
  const gap = OPS_SECURITY_POSTS.find((p) => p.state === 'coverage_gap')
  const handoverDue = OPS_SECURITY_POSTS.find((p) => p.state === 'handover_due')
  if (gap) {
    return {
      id: 'missing',
      question: 'Anyone missing?',
      sentence: `${gap.name} has no guard confirmed for next shift`,
      caption: `${muster} · ${gap.assignee} is on post now · shift ends in ${gap.shiftEndsIn}`,
      tone: 'danger',
      linkPath: '/operations/department',
    }
  }
  if (handoverDue) {
    return {
      id: 'missing',
      question: 'Anyone missing?',
      sentence: `${handoverDue.name} hands over soon`,
      caption: `${muster} · ${handoverDue.assignee} finishes in ${handoverDue.shiftEndsIn}`,
      tone: 'warning',
      linkPath: '/operations/department',
    }
  }
  return {
    id: 'missing',
    question: 'Anyone missing?',
    sentence: 'All posts covered.',
    caption: `${muster} · ${OPS_SECURITY_POSTS.length} posts · every guard confirmed`,
    tone: 'success',
    linkPath: '/operations/department',
  }
}

export const buildSiteCards = (): IOpsTodayQuestionCard[] => [
  buildPaceCard(),
  buildBrokenCard(),
  buildMissingCard(),
]

const buildMiningMachinesCard = (): IOpsTodayQuestionCard => {
  const available = OPS_MACHINES.filter((m) => m.state === 'active').length
  const availability = `${available} of ${OPS_MACHINES.length} machines available`
  if (OPS_MACHINES_DOWN.length === 0) {
    return {
      id: 'machines',
      question: 'Are the machines up?',
      sentence: 'No breakdowns reported this shift',
      caption: `${availability} · every machine is on the job`,
      tone: 'success',
      linkPath: '/operations/timeline',
      trend: availTrend(),
    }
  }
  return {
    id: 'machines',
    question: 'Are the machines up?',
    sentence: `${OPS_MACHINES_DOWN.map((m) => m.name).join(' and ')} are down`,
    caption: [
      availability,
      ...OPS_MACHINES_DOWN.map((m) => `${m.name} back ~${m.estimatedReturn}`),
    ].join(' · '),
    tone: 'danger',
    linkPath: '/operations/timeline',
    trend: availTrend(),
  }
}

const buildMiningCards = (): IOpsTodayQuestionCard[] => [
  buildPaceCard(),
  buildMiningMachinesCard(),
  {
    id: 'crew',
    question: 'Who is on shift?',
    sentence: 'Full mining crew on shift.',
    caption: '8 of 8 rostered · all checked in at 06:00',
    tone: 'success',
    linkPath: '/operations/department',
  },
]

const buildPlantCards = (): IOpsTodayQuestionCard[] => {
  const feedTask = OPS_LIVE_TASKS.find((t) => t.id === 9113)
  return [
    {
      id: 'throughput',
      question: 'Throughput vs plan',
      sentence: 'Feed running at 640 of 850 t/h — 25% down',
      caption: feedTask
        ? `Feed rate check dispatched at ${feedTask.generatedAt} · ${feedTask.operator} on it at ${feedTask.location}`
        : 'Feed target 850 t/h',
      tone: 'warning',
      linkPath: '/operations/plans',
    },
    {
      id: 'interruptions',
      question: 'Any feed interruptions?',
      sentence: 'Crusher feed reduced −25% since 07:43',
      caption: 'Haulage at 3 of 4 cycles — Truck 12 blocked on Main Haul Road',
      tone: 'warning',
      linkPath: '/operations/timeline',
    },
    {
      id: 'operators',
      question: 'Who is on it?',
      sentence: 'All plant operators present.',
      caption: '2 of 2 plant operators present · Grace M. on the feed rate check',
      tone: 'success',
      linkPath: '/operations/department',
    },
  ]
}

const buildMaintenanceCards = (): IOpsTodayQuestionCard[] => {
  const tyre = OPS_PARTS_RISK.find((p) => p.part.startsWith('Tyre'))
  const hose = OPS_PARTS_RISK.find((p) => p.part.startsWith('Hydraulic'))
  return [
    {
      id: 'workOrders',
      question: 'Are we on pace?',
      sentence: '1 of 3 workshop jobs done',
      caption: 'Hose replacement on Dozer D1 in progress · 500h generator service still waiting',
      tone: 'warning',
      linkPath: '/operations/timeline',
    },
    {
      id: 'parts',
      question: 'Anything stuck?',
      sentence: tyre
        ? 'Tyre 18.00R33 below reorder — needed for Truck 12'
        : 'No parts holding up work.',
      caption:
        tyre && hose
          ? `${tyre.stock} in stock · reorder point ${tyre.reorderPoint} · ${hose.part} also low (${hose.stock} of ${hose.reorderPoint})`
          : 'All stock above reorder points',
      tone: tyre ? 'warning' : 'success',
      linkPath: '/operations/department',
    },
    {
      id: 'fitters',
      question: 'Anyone missing?',
      sentence: '1 fitter on shift · MECH-2 gap',
      caption: 'Nicholas T. on the Dozer D1 hose job · next MECH-2 arrives with night shift',
      tone: 'warning',
      linkPath: '/operations/department',
    },
  ]
}

const buildCampCards = (): IOpsTodayQuestionCard[] => {
  const tasks = deptTasks('campServices')
  const done = tasks.filter((t) => taskCoreState(t.taskStatus) === 'COMPLETED').length
  const inProgress = tasks.find((t) => taskCoreState(t.taskStatus) === 'ACTIVE')
  const overdue = OPS_LOCATION_STATUS.filter((l) => l.state === 'overdue')
  const onSchedule = OPS_LOCATION_STATUS.length - overdue.length
  return [
    {
      id: 'campTasks',
      question: 'Are we on pace?',
      sentence: tasks.length
        ? `${done} of ${tasks.length} camp tasks done`
        : 'No tasks for this department this shift',
      caption: inProgress
        ? `${inProgress.name} in progress · ~${inProgress.etaMin} min left`
        : `${done} done · nothing in progress right now`,
      tone: 'success',
      linkPath: '/operations/timeline',
    },
    {
      id: 'facilities',
      question: 'Any facility faults?',
      sentence: overdue.length ? `${overdue[0].name} overdue` : 'All locations on schedule.',
      caption: overdue.length
        ? `Last done ${overdue[0].lastDone} · ${onSchedule} of ${OPS_LOCATION_STATUS.length} locations on schedule`
        : `${OPS_LOCATION_STATUS.length} of ${OPS_LOCATION_STATUS.length} locations on schedule`,
      tone: overdue.length ? 'warning' : 'success',
      linkPath: '/operations/department',
    },
    {
      id: 'campStaffing',
      question: 'Anyone missing?',
      sentence: '1 of 2 cleaners present',
      caption: 'Thomas P. on Kitchen and Mess Hall · Robert L. on break',
      tone: 'warning',
      linkPath: '/operations/department',
    },
  ]
}

const buildSecurityCards = (): IOpsTodayQuestionCard[] => {
  const patrol = deptTasks('security').find((t) => t.name.toLowerCase().includes('patrol'))
  return [
    {
      id: 'patrols',
      question: 'Are we on pace?',
      sentence: patrol ? 'Patrols on schedule.' : 'No patrols running right now',
      caption: patrol
        ? `0 of 4 patrols done · ${patrol.location} underway · ~${patrol.etaMin} min remaining`
        : '0 of 4 patrols done · next patrol generates on the hour',
      tone: 'success',
      linkPath: '/operations/timeline',
    },
    {
      id: 'incidents',
      question: 'Any incidents?',
      sentence: 'No incidents this shift',
      caption: '0 reported since 06:00 · visitor escort underway at Admin Office Block',
      tone: 'success',
      linkPath: '/operations/safety',
    },
    buildMissingCard(),
  ]
}

const buildCustomCards = (dept: IOpsDepartment): IOpsTodayQuestionCard[] => {
  const tasks = deptTasks(dept.key)
  const done = tasks.filter((t) => taskCoreState(t.taskStatus) === 'COMPLETED')
  const stuck = tasks.filter(
    (t) =>
      taskCoreState(t.taskStatus) === 'BLOCKED' ||
      (!t.operator && taskCoreState(t.taskStatus) !== 'COMPLETED'),
  )
  const operators: string[] = []
  tasks.forEach((t) => {
    if (t.operator && !operators.includes(t.operator)) operators.push(t.operator)
  })
  return [
    {
      id: 'deptPace',
      question: 'Are we on pace?',
      sentence: tasks.length
        ? `${done.length} of ${tasks.length} tasks done this shift`
        : 'No tasks for this department this shift',
      caption: tasks.length
        ? `${tasks.length - done.length} still to go before 18:00`
        : 'Nothing planned or generated since 06:00',
      tone: 'success',
      linkPath: '/operations/timeline',
    },
    {
      id: 'deptStuck',
      question: 'Anything stuck?',
      sentence: stuck.length
        ? `${count(stuck.length, 'task')} waiting on a decision`
        : 'Nothing stuck.',
      caption: stuck.length
        ? stuck
            .map((t) => `${t.name} — ${t.blockedReason || t.unassignedReason || 'unassigned'}`)
            .join(' · ')
        : 'No blocked or unassigned tasks this shift',
      tone: stuck.length ? 'warning' : 'success',
      linkPath: '/operations/department',
    },
    {
      id: 'deptCrew',
      question: 'Who is on it?',
      sentence: operators.length ? operators.join(', ') : 'No one assigned yet',
      caption: operators.length
        ? `${operators.length} assigned · ${count(tasks.length, 'task')} this shift`
        : tasks.length
        ? 'Tasks are waiting for the resolver to assign people'
        : 'People will appear here once tasks are assigned',
      tone: operators.length || !tasks.length ? 'success' : 'warning',
      linkPath: '/operations/department',
    },
  ]
}

export const buildDeptCards = (dept: IOpsDepartment): IOpsTodayQuestionCard[] => {
  if (dept.kpiTemplate === 'mining') return buildMiningCards()
  if (dept.kpiTemplate === 'plant') return buildPlantCards()
  if (dept.kpiTemplate === 'maintenance') return buildMaintenanceCards()
  if (dept.kpiTemplate === 'camp') return buildCampCards()
  if (dept.kpiTemplate === 'security') return buildSecurityCards()
  return buildCustomCards(dept)
}

export const buildInboxItems = (): IOpsTodayInboxItem[] => {
  const items: IOpsTodayInboxItem[] = []
  const risk = OPS_RISKS[0]
  if (risk) {
    const riskAlert = OPS_ALERTS.find((a) => a.planId === risk.planId && a.severity === 'critical')
    items.push({
      id: 1,
      tone: 'warning',
      department: 'mining',
      sentence: 'Swap Truck 07 in for 2 cycles while Truck 12 is repaired',
      source: `Suggested from North Pit Extraction risk · ${riskAlert ? riskAlert.time : '07:42'}`,
      detail: {
        why: `${risk.chain[0]} with a tyre fault — ${risk.chain[1].toLowerCase()}`,
        impact: 'Truck 07 leaves Waste Stripping for ~2h · that plan drops one cycle',
        whatHappens: 'Truck 07 is rebooked to North Pit for 2 cycles, then returns automatically',
      },
      actions: [
        {
          label: 'Confirm approve',
          variant: 'success',
          toastMessage: 'Done — Truck 07 will be rebooked',
        },
        { label: 'Dismiss', variant: 'black' },
      ],
    })
  }
  const gap = OPS_SECURITY_POSTS.find((p) => p.state === 'coverage_gap')
  if (gap) {
    const gapAlert = OPS_ALERTS.find((a) => a.title.includes('Coverage'))
    items.push({
      id: 2,
      tone: 'danger',
      department: 'security',
      sentence: `Find cover for ${gap.name} before 18:00`,
      source: `From the ${gap.name} security post · incoming guard unconfirmed · ${
        gapAlert ? gapAlert.time : '07:30'
      }`,
      detail: {
        why: `No incoming guard confirmed for ${gap.name} — ${gap.assignee} finishes in ${gap.shiftEndsIn}`,
        impact: 'An unmanned gate stops vehicle movement in and out of site at shift change',
        whatHappens:
          'You pick a replacement on the department board and the next-shift roster updates',
      },
      actions: [
        {
          label: 'Reassign now',
          variant: 'danger',
          toastMessage: 'Okay — pick a replacement on the department board',
          navigateTo: '/operations/department',
        },
        { label: 'Dismiss', variant: 'black' },
      ],
    })
  }
  const serviceTask = OPS_LIVE_TASKS.find((t) => t.id === 9108)
  if (serviceTask) {
    items.push({
      id: 3,
      tone: 'warning',
      department: 'maintenance',
      sentence: 'The 500h generator service needs a MECH-2 mechanic — none on this shift',
      source: `Waiting since ${serviceTask.generatedAt} · ${serviceTask.location}`,
      detail: {
        why: `Generator G2 crossed 500 engine hours — the service trigger fired at ${serviceTask.generatedAt}`,
        impact: 'Service is already 3h overdue · power redundancy reduced until it is done',
        whatHappens:
          'You review eligible mechanics — fatigue-checked overtime or a reassignment from the Workshop',
      },
      actions: [
        {
          label: 'See options',
          variant: 'secondary',
          navigateTo: `/operations/task/${serviceTask.id}`,
          keepsItem: true,
        },
        { label: 'Dismiss', variant: 'black' },
      ],
    })
  }
  const queueAlert = OPS_ALERTS.find((a) => a.title === 'Queue below replenish threshold')
  if (queueAlert) {
    items.push({
      id: 4,
      tone: 'info',
      department: 'mining',
      sentence:
        'North Pit is running low on queued work — the scheduler will top it up at the next run',
      source: `From North Pit Extraction · ${queueAlert.time}`,
      detail: {
        why: 'North Pit Extraction queue is at 2 tasks — below the replenish threshold of 3',
        impact: 'No production impact — trucks will not run out of work before the top-up',
        whatHappens:
          'The scheduler refills the queue to 4 of 4 on its next run — nothing for you to do',
      },
      actions: [{ label: 'OK', variant: 'secondary' }],
    })
  }
  items.push({
    id: 5,
    tone: 'warning',
    department: 'mining',
    sentence: 'Pit A diesel is down to 1.9 days of cover — reorder window is closing',
    source: 'From Fuel management · tank at 30% · burn 1,850 L/day',
    detail: {
      why: 'Burn rate 1,850 L/day against 3,600 L in tank',
      impact: 'Haul fleet refuels from Pit A — an empty tank stops haulage within a shift',
      whatHappens: 'A draft purchase request is created for review in Procurement',
    },
    actions: [
      {
        label: 'Draft purchase request',
        variant: 'success',
        toastMessage: 'Draft PR created — review in Procurement',
      },
      { label: 'Dismiss', variant: 'black' },
    ],
  })
  return items
}

export const deptInboxItems = (key: string): IOpsTodayInboxItem[] =>
  buildInboxItems().filter((i) => i.department === key)
